'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import * as Sentry from '@sentry/nextjs';
const sentryEnhancedRoutes = ['/', '/auth/login', '/auth/verify'];
let replayAttached = false;
let consoleLoggingAttached = false;
const shouldEnhanceRoute = (pathname: string | null) => pathname ? sentryEnhancedRoutes.includes(pathname) : false;
const scheduleIdleWork = (work: () => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }
  const idleWindow = window as Window & {
    requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
    cancelIdleCallback?: (handle: number) => void;
  };
  if (idleWindow.requestIdleCallback) {
    const id = idleWindow.requestIdleCallback(work, {
      timeout: 2500
    });
    return () => idleWindow.cancelIdleCallback?.(id);
  }
  const timeoutId = globalThis.setTimeout(work, 1500);
  return () => globalThis.clearTimeout(timeoutId);
};
export function SentryBootstrap() {
  const $ = _c(3);
  const pathname = usePathname();
  let t0;
  let t1;
  if ($[0] !== pathname) {
    t0 = () => {
      if (!shouldEnhanceRoute(pathname) || typeof window === "undefined") {
        return;
      }
      const attachIntegrations = () => {
        if (!replayAttached) {
          Sentry.addIntegration(Sentry.replayIntegration());
          replayAttached = true;
        }
        if (!consoleLoggingAttached) {
          Sentry.addIntegration(Sentry.consoleLoggingIntegration({
            levels: ["warn", "error"]
          }));
          consoleLoggingAttached = true;
        }
        window.removeEventListener("pointerdown", attachIntegrations);
        window.removeEventListener("keydown", attachIntegrations);
        window.removeEventListener("touchstart", attachIntegrations);
      };
      const cancelIdle = scheduleIdleWork(attachIntegrations);
      window.addEventListener("pointerdown", attachIntegrations, {
        once: true,
        passive: true
      });
      window.addEventListener("keydown", attachIntegrations, {
        once: true
      });
      window.addEventListener("touchstart", attachIntegrations, {
        once: true,
        passive: true
      });
      return () => {
        cancelIdle();
        window.removeEventListener("pointerdown", attachIntegrations);
        window.removeEventListener("keydown", attachIntegrations);
        window.removeEventListener("touchstart", attachIntegrations);
      };
    };
    t1 = [pathname];
    $[0] = pathname;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return null;
}